import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { DimensionEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { ReceiptFormTypes } from "models/formTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormFieldConstants } from "utils/constants";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { ArraySchema } from "models/utilTypes/yupSchemaTypes";
//#endregion
//#region components
import { useDimensionMaps } from "components/common";
import { costForwardingValidationFields } from "components/costForwardingMarking";
//#endregion
import * as Yup from "yup";
import { DimensionHelper, LocalizationHelperInstance, RegularExpressions, UtilsHelper } from "utils/helpers";
import { OTHER_PAYMENT_METHOD_ID } from "utils/constants/receiptConstants";

function useReceiptTodoFormHelpers(receipt: WintSuperkollAPIModel.Receipt, showFullForm?: boolean) {
  const { dimensions, forcedDimensionMap } = useDimensionMaps(DimensionEnums.DimensionCategory.Receipt);

  const hasDimensions = dimensions && dimensions.length > 0;
  const receiptCategories = useSelector((x: AppState) => x.auth.selectedCompany.receiptCategories ?? []);

  const initialValues = React.useMemo(
    (): ReceiptFormTypes.Todo => ({
      dimensions: hasDimensions ? DimensionHelper.getValidList(receipt.dimensionList ?? [], dimensions) : [],
      showDimensions: hasDimensions,
      canPayEmployee: receipt.canPayEmployee,
      personNumber: (receipt.personnumber ?? "").replace("-", ""),
      bankAccount: receipt.recipientAccount ?? "",
      clearingNumber: receipt.recipientClearingNumber ?? "",
      isManualPayment: receipt.hasMissingPaymentDetails,
      amount: receipt.amount,
      currency: receipt.currency,
      paymentMethodId: receipt.paymentMethodId ?? OTHER_PAYMENT_METHOD_ID,
      paymentMethod: receipt.paymentMethodName ?? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Other),
      paymentMethodType: receipt.paymentMethodType,
      purchaseDate: receipt.dateTime,
      receiptCategoryId: receipt.receiptCategoryId,
      receiptCategory: receipt.categoryName,
      receiptCategoryType: receiptCategories.find((r) => r.id === receipt.receiptCategoryId)?.categoryType,
      intent: receipt.intent ?? "",
      otherExplanation: receipt.intent ?? "",
      isOtherPaymentMethod: receipt.isOtherPaymentMethod,
      otherPaymentMethodExplanation: receipt.otherPaymentMethodExplanation,
      persons:
        receipt.persons?.map((p) => ({
          ...p,
          name: p.name ?? "",
          company: p.company ?? "",
          id: UtilsHelper.getUniqueId(),
        })) ?? [],
      filesToUpload: showFullForm
        ? receipt.attachments?.map<UploadedFile>(
            (attachment): UploadedFile => ({
              ...attachment,
              contentType: attachment.contentType ?? "",
              fileName: attachment.fileName ?? "",
              id: attachment.id,
              fileSize: 0,
              data: attachment.data ?? "",
            })
          )
        : undefined,
    }),
    [receipt, receiptCategories, dimensions]
  );

  const validationSchema = Yup.object<ReceiptFormTypes.Todo>({
    dimensions: Yup.array()
      .of(
        Yup.object<WintSuperkollAPIModel.DimensionData>({
          dimensionId: Yup.string(),
          amount: Yup.number(),
        })
      )
      // eslint-disable-next-line no-empty-pattern
      .when([], ([], schema: ArraySchema<WintSuperkollAPIModel.DimensionData>) =>
        forcedDimensionMap.length > 0 ? schema.required("") : schema
      )
      .checkForcedDimensions(forcedDimensionMap)
      .when(
        FormFieldConstants.Receipt.Todo.Amount,
        ([amount]: [number], schema: ArraySchema<WintSuperkollAPIModel.DimensionData>) =>
          schema.validateDimensionAmounts(dimensions, amount)
      ),
    showDimensions: Yup.boolean(),
    canPayEmployee: Yup.boolean(),
    isManualPayment: Yup.boolean(),
    amount: Yup.number()
      .required(TranslationKeys.Validations.Required)
      .when([FormFieldConstants.Receipt.Details.PaymentMethodType], ([paymentMethod], schema: Yup.NumberSchema) =>
        paymentMethod !== WintSuperkollAPIModel.ReceiptPaymentMethodType.WintCard
          ? schema.min(0.01, TranslationKeys.Validations.Required)
          : schema
      ),
    currency: Yup.string().required(TranslationKeys.Validations.Required),
    purchaseDate: Yup.date()
      .required(TranslationKeys.Validations.Required)
      .typeError(TranslationKeys.Validations.InvalidDate),
    paymentMethodId: Yup.number()
      .nullable()
      .when(FormFieldConstants.Receipt.Details.IsOtherPaymentMethod, {
        is: true,
        then: (schema) => schema.nullable(),
        otherwise: (schema) => schema.required(TranslationKeys.Validations.Required),
      }),
    personNumber: Yup.string()
      .matches(RegularExpressions.PersonNumber, {
        message: TranslationKeys.Validations.InvalidPersonNumber,
        excludeEmptyString: true,
      })
      .when(
        [
          FormFieldConstants.Receipt.Todo.CanPayEmployee,
          FormFieldConstants.Receipt.Todo.IsManualPayment,
          FormFieldConstants.Receipt.Details.PaymentMethodType,
        ],
        ([canPayEmployee, isManualPayment, paymentMethod], schema: Yup.StringSchema) =>
          paymentMethod === WintSuperkollAPIModel.ReceiptPaymentMethodType.OwnExpense &&
          !canPayEmployee &&
          !isManualPayment
            ? schema.required(TranslationKeys.Validations.Required)
            : schema
      ),
    bankAccount: Yup.string().when(
      [
        FormFieldConstants.Receipt.Todo.CanPayEmployee,
        FormFieldConstants.Receipt.Todo.IsManualPayment,
        FormFieldConstants.Receipt.Details.PaymentMethodType,
      ],
      ([canPayEmployee, isManualPayment, paymentMethod], schema: Yup.StringSchema) =>
        paymentMethod === WintSuperkollAPIModel.ReceiptPaymentMethodType.OwnExpense &&
        !canPayEmployee &&
        !isManualPayment
          ? schema.required(TranslationKeys.Validations.Required)
          : schema
    ),
    clearingNumber: Yup.string().when(
      [
        FormFieldConstants.Receipt.Todo.CanPayEmployee,
        FormFieldConstants.Receipt.Todo.IsManualPayment,
        FormFieldConstants.Receipt.Details.PaymentMethodType,
      ],
      ([canPayEmployee, isManualPayment, paymentMethod], schema: Yup.StringSchema) =>
        paymentMethod === WintSuperkollAPIModel.ReceiptPaymentMethodType.OwnExpense &&
        !canPayEmployee &&
        !isManualPayment
          ? schema.required(TranslationKeys.Validations.Required)
          : schema
    ),
    receiptCategoryId: Yup.number().required(TranslationKeys.Validations.Required),
    intent: Yup.string().when(
      FormFieldConstants.Receipt.Todo.ReceiptCategoryType,
      ([value], schema: Yup.StringSchema) =>
        ((value === WintSuperkollAPIModel.ReceiptCategoryType.Representation ||
          value === WintSuperkollAPIModel.ReceiptCategoryType.EmployeeRepresentation) &&
          schema.required(TranslationKeys.Validations.Required)) ||
        schema
    ),
    persons: Yup.array()
      .of(
        Yup.object({
          id: Yup.number(),
          name: Yup.string().required(TranslationKeys.Validations.Required),
          company: Yup.string().required(TranslationKeys.Validations.Required),
        })
      )
      .when(
        FormFieldConstants.Receipt.Todo.ReceiptCategoryType,
        (
          [value]: [WintSuperkollAPIModel.ReceiptCategoryType],
          schema: ArraySchema<WintSuperkollAPIModel.ReceiptPerson>
        ) =>
          ((value === WintSuperkollAPIModel.ReceiptCategoryType.Representation ||
            value === WintSuperkollAPIModel.ReceiptCategoryType.EmployeeRepresentation) &&
            schema.min(1, TranslationKeys.Validations.Required).required(TranslationKeys.Validations.Required)) ||
          schema
      ),
    otherExplanation: Yup.string().when(
      FormFieldConstants.Receipt.Todo.ReceiptCategoryType,
      ([value], schema: Yup.StringSchema) =>
        (value === WintSuperkollAPIModel.ReceiptCategoryType.Other &&
          schema.required(TranslationKeys.Validations.Required)) ||
        schema
    ),
    filesToUpload: Yup.array()
      .of(
        Yup.object<UploadedFile>({
          contentType: Yup.string(),
          fileName: Yup.string(),
          fileSize: Yup.number(),
          data: Yup.string(),
        })
      )
      // eslint-disable-next-line no-empty-pattern
      .when([], ([], schema: ArraySchema<UploadedFile>) =>
        showFullForm ? schema.required(TranslationKeys.Validations.Required) : schema
      ),
    ...costForwardingValidationFields,
  });

  return { validationSchema, initialValues };
}

export default useReceiptTodoFormHelpers;
